import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline";

const TableComponent = ({
  headers = [],
  data = [],
  itemsPerPage = 10,
  settings,
  contentHeight = "66vh",
  rowKey,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  const getCellValue = (row, key) => row?.[key];

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const dir = sortConfig.direction === "asc" ? 1 : -1;

    return [...data].sort((a, b) => {
      const av = getCellValue(a, sortConfig.key);
      const bv = getCellValue(b, sortConfig.key);

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      const aNum = typeof av === "number" || !isNaN(Number(av));
      const bNum = typeof bv === "number" || !isNaN(Number(bv));

      if (aNum && bNum) {
        const na = Number(av);
        const nb = Number(bv);
        if (na < nb) return -1 * dir;
        if (na > nb) return 1 * dir;
        return 0;
      }

      const sa = String(av).toLocaleLowerCase();
      const sb = String(bv).toLocaleLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
  }, [data, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));

  const currentData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage((prev) => {
      const total = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
      return prev > total ? total : prev;
    });
  }, [sortedData, itemsPerPage]);

  const handlePageChange = (pageNumber) => {
    const p = Math.min(Math.max(pageNumber, 1), totalPages);
    setCurrentPage(p);
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const visibleHeaders = useMemo(
    () =>
      headers.filter((h) =>
        typeof h.visible === "function"
          ? currentData.some((item) => h.visible(item))
          : h.visible !== false,
      ),
    [headers, currentData],
  );

  return (
    <div className="w-full">
      <div className="overflow-x-auto border rounded-lg relative">
        <div className="overflow-y-auto" style={{ maxHeight: contentHeight }}>
          <table className="w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-base-200">
              <tr>
                {visibleHeaders.map((header) => {
                  const isSorted = sortConfig.key === header.key;
                  const isAsc = sortConfig.direction === "asc";
                  return (
                    <th
                      key={header.key}
                      className="px-4 py-2 bg-base-200 border-b cursor-pointer select-none"
                      onClick={() => handleSort(header.key)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{header.label}</span>
                        {isSorted &&
                          (isAsc ? (
                            <ArrowUpIcon className="w-4 h-4 text-blue-500" />
                          ) : (
                            <ArrowDownIcon className="w-4 h-4 text-blue-500" />
                          ))}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {currentData.map((item, index) => (
                <tr
                  key={rowKey ? rowKey(item, index) : (item?.id ?? index)}
                  className="hover:bg-base-100"
                >
                  {visibleHeaders.map((header) => (
                    <td
                      key={header.key}
                      className="px-4 py-2 border-b align-top"
                    >
                      {header.key === "setting" && settings?.renderSettings
                        ? settings.renderSettings(item, index)
                        : header.render
                          ? header.render(item, index)
                          : item?.[header.key]}
                    </td>
                  ))}
                </tr>
              ))}

              {currentData.length === 0 && (
                <tr>
                  <td
                    className="px-4 py-6 text-center text-sm text-gray-500"
                    colSpan={visibleHeaders.length}
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sticky bottom-0 z-20 bg-white">
        <div className="flex items-center justify-between mt-4 px-1 sm:px-4 mb-2">
          <div className="text-sm text-gray-500">
            Hiển thị {currentData.length} của {data.length}
          </div>

          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-xs sm:text-sm sm:p-2.5"
              aria-label="Trang đầu"
            >
              <ChevronLeftIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              <ChevronLeftIcon className="w-3 h-3 sm:w-4 sm:h-4 -ml-1" />
            </button>

            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm sm:p-2.5"
              aria-label="Trang trước"
            >
              <ChevronLeftIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>

            <span className="px-3 py-1 text-xs font-medium text-gray-700 border rounded-md sm:text-sm sm:px-4 sm:py-2">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm sm:p-2.5"
              aria-label="Trang sau"
            >
              <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>

            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded-md shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-xs sm:text-sm sm:p-2.5"
              aria-label="Trang cuối"
            >
              <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4 -ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableComponent;
